;(function (root, _) {
  'use strict'

  // @note `validatePassword` treats `history_length` as a recognized name, but
  //   doesn't implement a test for it, because it requires database access,
  //   and we don't want to provide that information to the browser because it
  //   would allow malicious actors to determine what a user's current password
  //   is.
  var knownRuleNames = [
    'character_patterns',
    'history_length',
    'password_length',
  ]
  // Rules that only require a single test. (Unlike `character_patterns`.)
  var trivialRuleNames = ['password_length']

  /**
   * Count how many rules the password must pass.
   *
   * @param {Object} rules - The collection of password rules.
   * @returns {number} The number of tests that will be run.
   *
   * @access private
   */
  function getExpectedSuccessCount(rules) {
    var rulesKeys = Object.keys(rules)
    var total = _.intersection(rulesKeys, trivialRuleNames).length

    // @note This must be handled differently than `password_length`, because
    //   `character_patterns` is an array, and thus can have multiple tests.
    if (_.includes(rulesKeys, 'character_patterns'))
      total += rules.character_patterns.length

    return total
  }

  /**
   * Sanitize a regular expression so that every character is either in
   * the Latin alphabet (A-Z) or an underscore.
   *
   * @param {(regexp|string)} pattern - The regular expression to sanitize.
   * @returns {string} The sanitized regular expression.
   *
   * @access private
   */
  function sanitizeRegExp(pattern) {
    var asString = _.isRegExp(pattern) ? pattern.source : pattern

    var sanitized = asString.replace(/[^a-zA-Z]/g, '_')

    return sanitized
  }

  /**
   * Test whether the password passes a given rule.
   *
   * @param {*} data - The data for the rule.
   * @param {string} name - The name of the rule.
   * @param {string} password - The password to test.
   * @returns {boolean} Whether the password passed the rule test.
   *
   * @access private
   */
  function testRule(data, name, password) {
    var result

    switch (name) {
      // @todo <jordan@workwithopal.com> This may need to support a plain
      //   string, in addition to the support for an array.
      case 'character_patterns':
        // 1. Make sure the rule is a regular expression object. (It may
        //   be a string, or it may be a regular expression literal.) (Bonus:
        //   a stringified regular expression may be over-escaped -- for
        //   example, `"[\\\\d\\\\W]"`. The `.replace` fixes the extra escaping
        //   so the expression will evaluate correctly.
        // 2. Test the password against the regular expression.
        result = new RegExp(data.replace(/\\{2}/g, '\\')).test(password)
        break
      case 'password_length':
        result = password.length >= data
        break
      default:
        result = null
    }

    return result
  }

  /**
   * Test whether a password matches a set of password rules.
   *
   * @param {string} password - The password to test.
   * @param {Object} customRules - The rules that will
   *   be used to test the password.
   * @returns {Array<boolean, Array<string>, Array<string>>} Whether the password is valid,
   *   and a list of rule tests that the password passed.
   *
   * @example
   *   validatePassword('test', { password_length: 5 })
   *   //=> [false, [], ['password_length']]
   *   validatePassword('test!', { character_patterns: ['\\d'] })
   *   //=> [false, [], ['character_patterns_d']]
   *   validatePassword('test!', { character_patterns: ['\\d'], password_length: 5 })
   *   //=> [false, ['password_length'], ['character_patterns_d']]
   *   validatePassword('test!f00', { character_patterns: ['\\d\\W'], password_length: 5, oink: 'test' })
   *   //=> [true, ['character_patterns_d_W', 'password_length'], []]
   *
   * @access public
   */
  function validatePassword(password, customRules) {
    var successfulRules = []
    var failedRules = []
    var defaultResult = [false, successfulRules, failedRules]
    var rules = _.isObject(customRules) ? customRules : {}
    var expectedSuccessCount = getExpectedSuccessCount(rules)

    if (!_.isString(password) || password === '') return defaultResult
    if (Object.keys(rules).length === 0) return defaultResult

    // The naïve approach of `_.filter` doesn't work because it only returns
    // an array of values: `_.filter({ test: 1 })` returns `[1]`, and the
    // property name is inaccessible.
    //
    // @see http://stackoverflow.com/a/16987864/672403
    _.each(rules, function (ruleData, ruleName) {
      var passed = false

      // Process rules in the `character_patterns` array individually so
      // outside code can tell which tests the password has passed.
      if (ruleName === 'character_patterns') {
        _.each(ruleData, function (pattern) {
          passed = testRule(pattern, ruleName, password)
          var formattedName = ruleName + sanitizeRegExp(pattern)
          if (passed) successfulRules.push(formattedName)
          else failedRules.push(formattedName)
        })
      } else {
        // Skip unrecognized rules.
        if (!knownRuleNames.includes(ruleName)) return

        passed = testRule(ruleData, ruleName, password)
        if (passed) successfulRules.push(ruleName)
        else failedRules.push(ruleName)
      }
    })

    return [
      successfulRules.length === expectedSuccessCount,
      successfulRules,
      failedRules,
    ]
  }
  root.validatePassword = validatePassword
})(window, _)
